/**
 * VERA Evidence Viewer Page
 * Full evidence detail view with 3D PLY viewer, metadata, and chain of custody
 */
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  makeStyles,
  tokens,
  Text,
  Card,
  Badge,
  Button,
  Divider,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbButton,
  BreadcrumbDivider,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "@fluentui/react-components";
import {
  ArrowDownload24Regular,
  Cube24Regular,
  Image24Regular,
  Video24Regular,
  DocumentBulletList24Regular,
  Clock24Regular,
  Person24Regular,
  Location24Regular,
  Info24Regular,
  Shield24Regular,
  Tag24Regular,
  ArrowLeft24Regular,
  Box24Regular,
  BarcodeScanner24Regular,
  Beaker24Regular,
  Color24Regular,
  Money24Regular,
  Archive24Regular,
} from "@fluentui/react-icons";

import { getCaseById, getEvidenceById } from "../data/mockData";
import { EvidenceViewer3D } from "../components/EvidenceViewer3D";
import type { EvidenceCategory, EvidenceStatus } from "../types";

const categoryLabels: Record<EvidenceCategory, string> = {
  "3d-scan": "3D Scan",
  photograph: "Photograph",
  video: "Video",
  document: "Document",
  audio: "Audio",
};

const categoryIcons: Record<EvidenceCategory, React.ReactNode> = {
  "3d-scan": <Cube24Regular />,
  photograph: <Image24Regular />,
  video: <Video24Regular />,
  document: <DocumentBulletList24Regular />,
  audio: <DocumentBulletList24Regular />,
};

const statusColors: Record<
  EvidenceStatus,
  "success" | "warning" | "important" | "informative"
> = {
  "in-custody": "success",
  "checked-out": "warning",
  "in-lab": "informative",
  released: "important",
  disposed: "informative",
};

const statusLabels: Record<EvidenceStatus, string> = {
  "in-custody": "In Custody",
  "checked-out": "Checked Out",
  "in-lab": "In Lab",
  released: "Released",
  disposed: "Disposed",
};

const formatFileSize = (mb: number): string => {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(1)} MB`;
};

const useStyles = makeStyles({
  container: {
    maxWidth: "1400px",
    margin: "0 auto",
    padding: tokens.spacingHorizontalL,
  },
  breadcrumbs: {
    marginBottom: tokens.spacingVerticalM,
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: "24px",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  backButton: {
    marginRight: "4px",
  },
  headerInfo: {
    display: "flex",
    flexDirection: "column",
  },
  itemNumber: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#004C97",
    fontFamily: "'Poppins', sans-serif",
    letterSpacing: "0.5px",
  },
  evidenceName: {
    fontSize: "22px",
    fontWeight: 600,
    color: "#2D2926",
    fontFamily: "'Poppins', sans-serif",
    marginTop: "2px",
  },
  headerActions: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  mainGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 380px",
    gap: "24px",
  },
  viewerPanel: {
    borderRadius: "12px",
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    height: "560px",
  },
  sidePanel: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  metadataCard: {
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    padding: "20px",
  },
  cardTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#2D2926",
    fontFamily: "'Poppins', sans-serif",
    marginBottom: "16px",
  },
  metaRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    marginBottom: "14px",
  },
  metaIcon: {
    color: "#004C97",
    flexShrink: 0,
    marginTop: "2px",
  },
  metaContent: {
    display: "flex",
    flexDirection: "column",
  },
  metaLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#63666A",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "2px",
  },
  metaValue: {
    fontSize: "13px",
    color: "#2D2926",
    lineHeight: "1.4",
  },
  tagsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginTop: "4px",
  },
  tag: {
    fontSize: "11px",
    color: "#004C97",
    backgroundColor: "rgba(0, 76, 151, 0.08)",
    padding: "3px 10px",
    borderRadius: "4px",
    fontWeight: 500,
  },
  custodySection: {
    gridColumn: "1 / -1",
    marginTop: "8px",
  },
  custodyCard: {
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    padding: "20px",
  },
  custodyTable: {
    width: "100%",
  },
  descriptionSection: {
    gridColumn: "1 / -1",
  },
  descriptionCard: {
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    padding: "20px",
  },
  descriptionText: {
    fontSize: "14px",
    color: "#2D2926",
    lineHeight: "1.7",
  },
  notFound: {
    textAlign: "center" as const,
    padding: "80px 20px",
  },
  viewerPlaceholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    backgroundColor: "#f1f3f4",
    color: "#63666A",
    gap: "12px",
  },
});

export const EvidenceViewerPage: React.FC = () => {
  const styles = useStyles();
  const { caseId, evidenceId } = useParams<{
    caseId: string;
    evidenceId: string;
  }>();
  const navigate = useNavigate();

  const caseData = caseId ? getCaseById(caseId) : undefined;
  const evidence = evidenceId ? getEvidenceById(evidenceId) : undefined;

  if (!caseData || !evidence) {
    return (
      <div className={styles.container}>
        <div className={styles.notFound}>
          <Text size={700} weight="semibold" block>
            Evidence Not Found
          </Text>
          <Text
            size={400}
            block
            style={{ marginTop: "8px", color: "#63666A" }}
          >
            The requested evidence item could not be located.
          </Text>
          <Button
            appearance="primary"
            onClick={() => navigate(caseId ? `/case/${caseId}` : "/")}
            style={{ marginTop: "20px" }}
          >
            Return to Case
          </Button>
        </div>
      </div>
    );
  }

  const is3DViewable =
    evidence.category === "3d-scan" &&
    (evidence.format === "ply" || evidence.format === "glb");

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = evidence.fileUrl;
    // Build a filename from the evidence name + format extension
    const safeName = evidence.name.replace(/[^a-zA-Z0-9_\-. ]/g, "").replace(/\s+/g, "_");
    link.download = `${safeName}.${evidence.format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <Breadcrumb className={styles.breadcrumbs}>
        <BreadcrumbItem>
          <BreadcrumbButton onClick={() => navigate("/")}>
            Dashboard
          </BreadcrumbButton>
        </BreadcrumbItem>
        <BreadcrumbDivider />
        <BreadcrumbItem>
          <BreadcrumbButton onClick={() => navigate(`/case/${caseId}`)}>
            {caseData.caseNumber}
          </BreadcrumbButton>
        </BreadcrumbItem>
        <BreadcrumbDivider />
        <BreadcrumbItem>
          <BreadcrumbButton current>
            Item #{evidence.itemNumber}
          </BreadcrumbButton>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Header */}
      <div className={styles.headerRow}>
        <div className={styles.headerLeft}>
          <Button
            appearance="subtle"
            icon={<ArrowLeft24Regular />}
            className={styles.backButton}
            onClick={() => navigate(`/case/${caseId}`)}
          />
          <div className={styles.headerInfo}>
            <Text className={styles.itemNumber}>
              Item #{evidence.itemNumber} • {caseData.caseNumber}
            </Text>
            <Text className={styles.evidenceName}>{evidence.name}</Text>
          </div>
        </div>
        <div className={styles.headerActions}>
          <Badge
            size="large"
            appearance="filled"
            color={statusColors[evidence.status]}
          >
            {statusLabels[evidence.status]}
          </Badge>
          <Button
            appearance="primary"
            icon={<ArrowDownload24Regular />}
            onClick={handleDownload}
          >
            Download ({formatFileSize(evidence.fileSizeMB)})
          </Button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className={styles.mainGrid}>
        {/* 3D Viewer / Preview */}
        <div className={styles.viewerPanel}>
          {is3DViewable ? (
            <EvidenceViewer3D
              fileUrl={evidence.fileUrl}
              format={evidence.format}
              evidenceName={evidence.name}
              vertexCount={evidence.vertexCount}
              fileSizeMB={evidence.fileSizeMB}
              demo={!evidence.hasRealFile}
            />
          ) : (
            <div className={styles.viewerPlaceholder}>
              <div style={{ fontSize: "48px" }}>
                {categoryIcons[evidence.category]}
              </div>
              <Text style={{ color: "#63666A" }}>
                {categoryLabels[evidence.category]} Preview
              </Text>
              <Text style={{ color: "#8a8d91", fontSize: "13px" }}>
                .{evidence.format.toUpperCase()} file — click Download to access
              </Text>
            </div>
          )}
        </div>

        {/* Side Panel - Metadata */}
        <div className={styles.sidePanel}>
          {/* Evidence Details */}
          <Card className={styles.metadataCard}>
            <Text className={styles.cardTitle}>Evidence Details</Text>

            <div className={styles.metaRow}>
              <Info24Regular className={styles.metaIcon} />
              <div className={styles.metaContent}>
                <Text className={styles.metaLabel}>Category</Text>
                <Text className={styles.metaValue}>
                  {categoryLabels[evidence.category]}
                </Text>
              </div>
            </div>

            <div className={styles.metaRow}>
              <Info24Regular className={styles.metaIcon} />
              <div className={styles.metaContent}>
                <Text className={styles.metaLabel}>Format</Text>
                <Text className={styles.metaValue}>
                  .{evidence.format.toUpperCase()} •{" "}
                  {formatFileSize(evidence.fileSizeMB)}
                </Text>
              </div>
            </div>

            {evidence.captureDevice && (
              <div className={styles.metaRow}>
                <Cube24Regular className={styles.metaIcon} />
                <div className={styles.metaContent}>
                  <Text className={styles.metaLabel}>Capture Device</Text>
                  <Text className={styles.metaValue}>
                    {evidence.captureDevice}
                  </Text>
                </div>
              </div>
            )}

            {evidence.vertexCount && (
              <div className={styles.metaRow}>
                <Info24Regular className={styles.metaIcon} />
                <div className={styles.metaContent}>
                  <Text className={styles.metaLabel}>
                    {evidence.format === "glb" ? "3D Model" : "Point Cloud"}
                  </Text>
                  <Text className={styles.metaValue}>
                    {(evidence.vertexCount / 1000000).toFixed(1)}M vertices
                    {evidence.faceCount
                      ? ` • ${(evidence.faceCount / 1000000).toFixed(1)}M faces`
                      : ""}
                  </Text>
                </div>
              </div>
            )}
          </Card>

          {/* Collection Info */}
          <Card className={styles.metadataCard}>
            <Text className={styles.cardTitle}>Collection Information</Text>

            <div className={styles.metaRow}>
              <Person24Regular className={styles.metaIcon} />
              <div className={styles.metaContent}>
                <Text className={styles.metaLabel}>Collected By</Text>
                <Text className={styles.metaValue}>
                  {evidence.collectedBy}
                </Text>
              </div>
            </div>

            <div className={styles.metaRow}>
              <Clock24Regular className={styles.metaIcon} />
              <div className={styles.metaContent}>
                <Text className={styles.metaLabel}>Collection Date</Text>
                <Text className={styles.metaValue}>
                  {new Date(evidence.collectedDate).toLocaleString()}
                </Text>
              </div>
            </div>

            <div className={styles.metaRow}>
              <Location24Regular className={styles.metaIcon} />
              <div className={styles.metaContent}>
                <Text className={styles.metaLabel}>Collection Location</Text>
                <Text className={styles.metaValue}>
                  {evidence.collectionLocation}
                </Text>
              </div>
            </div>
          </Card>

          {/* Physical Evidence Details */}
          {(evidence.make || evidence.propertyType || evidence.storageLocation) && (
            <Card className={styles.metadataCard}>
              <Text className={styles.cardTitle}>Physical Evidence</Text>

              {evidence.propertyType && (
                <div className={styles.metaRow}>
                  <Box24Regular className={styles.metaIcon} />
                  <div className={styles.metaContent}>
                    <Text className={styles.metaLabel}>Property Type</Text>
                    <Text className={styles.metaValue}>{evidence.propertyType}</Text>
                  </div>
                </div>
              )}

              {(evidence.make || evidence.model) && (
                <div className={styles.metaRow}>
                  <Info24Regular className={styles.metaIcon} />
                  <div className={styles.metaContent}>
                    <Text className={styles.metaLabel}>Make / Model</Text>
                    <Text className={styles.metaValue}>
                      {[evidence.make, evidence.model].filter(Boolean).join(" — ")}
                    </Text>
                  </div>
                </div>
              )}

              {evidence.serialNumber && (
                <div className={styles.metaRow}>
                  <BarcodeScanner24Regular className={styles.metaIcon} />
                  <div className={styles.metaContent}>
                    <Text className={styles.metaLabel}>Serial Number</Text>
                    <Text className={styles.metaValue}>{evidence.serialNumber}</Text>
                  </div>
                </div>
              )}

              {evidence.color && (
                <div className={styles.metaRow}>
                  <Color24Regular className={styles.metaIcon} />
                  <div className={styles.metaContent}>
                    <Text className={styles.metaLabel}>Color / Description</Text>
                    <Text className={styles.metaValue}>{evidence.color}</Text>
                  </div>
                </div>
              )}

              {evidence.condition && (
                <div className={styles.metaRow}>
                  <Shield24Regular className={styles.metaIcon} />
                  <div className={styles.metaContent}>
                    <Text className={styles.metaLabel}>Condition</Text>
                    <Text className={styles.metaValue}>{evidence.condition}</Text>
                  </div>
                </div>
              )}

              {evidence.estimatedValue !== undefined && evidence.estimatedValue > 0 && (
                <div className={styles.metaRow}>
                  <Money24Regular className={styles.metaIcon} />
                  <div className={styles.metaContent}>
                    <Text className={styles.metaLabel}>Estimated Value</Text>
                    <Text className={styles.metaValue}>
                      ${evidence.estimatedValue.toLocaleString()}
                    </Text>
                  </div>
                </div>
              )}

              {evidence.barcodeTag && (
                <div className={styles.metaRow}>
                  <Tag24Regular className={styles.metaIcon} />
                  <div className={styles.metaContent}>
                    <Text className={styles.metaLabel}>Evidence Tag #</Text>
                    <Text className={styles.metaValue}>{evidence.barcodeTag}</Text>
                  </div>
                </div>
              )}

              {evidence.storageLocation && (
                <div className={styles.metaRow}>
                  <Archive24Regular className={styles.metaIcon} />
                  <div className={styles.metaContent}>
                    <Text className={styles.metaLabel}>Storage Location</Text>
                    <Text className={styles.metaValue}>{evidence.storageLocation}</Text>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Recovery Details */}
          {(evidence.recoveredBy || evidence.recoveryLocation) && (
            <Card className={styles.metadataCard}>
              <Text className={styles.cardTitle}>Recovery Details</Text>

              {evidence.recoveredBy && (
                <div className={styles.metaRow}>
                  <Person24Regular className={styles.metaIcon} />
                  <div className={styles.metaContent}>
                    <Text className={styles.metaLabel}>Recovered By</Text>
                    <Text className={styles.metaValue}>{evidence.recoveredBy}</Text>
                  </div>
                </div>
              )}

              {evidence.recoveryLocation && (
                <div className={styles.metaRow}>
                  <Location24Regular className={styles.metaIcon} />
                  <div className={styles.metaContent}>
                    <Text className={styles.metaLabel}>Recovery Location</Text>
                    <Text className={styles.metaValue}>{evidence.recoveryLocation}</Text>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Lab / Forensics */}
          {evidence.labSubmitted && (
            <Card className={styles.metadataCard}>
              <Text className={styles.cardTitle}>Lab & Forensics</Text>

              <div className={styles.metaRow}>
                <Beaker24Regular className={styles.metaIcon} />
                <div className={styles.metaContent}>
                  <Text className={styles.metaLabel}>Lab Status</Text>
                  <Text className={styles.metaValue}>Submitted</Text>
                </div>
              </div>

              {evidence.labNotes && (
                <div className={styles.metaRow}>
                  <Info24Regular className={styles.metaIcon} />
                  <div className={styles.metaContent}>
                    <Text className={styles.metaLabel}>Lab Notes</Text>
                    <Text className={styles.metaValue}>{evidence.labNotes}</Text>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Tags */}
          <Card className={styles.metadataCard}>
            <Text className={styles.cardTitle}>Tags</Text>
            <div className={styles.tagsContainer}>
              {evidence.tags.map((tag) => (
                <span key={tag} className={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
          </Card>
        </div>

        {/* Description - Full Width */}
        <div className={styles.descriptionSection}>
          <Card className={styles.descriptionCard}>
            <Text className={styles.cardTitle}>Description</Text>
            <Text className={styles.descriptionText}>
              {evidence.description}
            </Text>
          </Card>
        </div>

        {/* Chain of Custody - Full Width */}
        <div className={styles.custodySection}>
          <Card className={styles.custodyCard}>
            <Text className={styles.cardTitle}>
              Chain of Custody ({evidence.chainOfCustody.length} events)
            </Text>
            <Table className={styles.custodyTable}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Date/Time</TableHeaderCell>
                  <TableHeaderCell>Action</TableHeaderCell>
                  <TableHeaderCell>Officer</TableHeaderCell>
                  <TableHeaderCell>Badge #</TableHeaderCell>
                  <TableHeaderCell>Location</TableHeaderCell>
                  <TableHeaderCell>Notes</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {evidence.chainOfCustody.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>
                      {new Date(event.timestamp).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Text weight="semibold">{event.action}</Text>
                    </TableCell>
                    <TableCell>{event.officer}</TableCell>
                    <TableCell>{event.badgeNumber}</TableCell>
                    <TableCell>{event.location}</TableCell>
                    <TableCell>
                      {event.notes || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EvidenceViewerPage;
